import React, { useState, useEffect, useCallback } from 'react';
import { 
  TrashIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/solid';
import { unitCodesApi, authFetch, apiUrl, authService, courseTreeApi } from '../utils/api';
import { useUser } from '../contexts/UserContext';
import {
  GetUnitCodeDto,
  StudentWithUnitCodesDto,
  UnitCodeFilters,
  TERM_OPTIONS,
  SortConfig,
  GenerateCodeFormData,
  GenerateGeneralCodeFormData,
  CourseTreeUnit,
  SortableFields,
  Teacher,
  Subject,
  CourseNew
} from '../types/unitCode';

interface Grade {
  id: string;
  name: string;
  description: string;
  level: number;
  code: string;
  active: boolean;
}


type ActiveTab = 'codes' | 'students' | 'general-codes';

const UnitCodes: React.FC = () => {
  const { user, isTeacher, isModerator, getTeacherId } = useUser();
  
  // State management
  const [activeTab, setActiveTab] = useState<ActiveTab>('codes');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // Data
  const [codes, setCodes] = useState<GetUnitCodeDto[]>([]);
  const [students, setStudents] = useState<StudentWithUnitCodesDto[]>([]);
  const [generalCodes, setGeneralCodes] = useState<GetUnitCodeDto[]>([]);
  
  // Filters
  const [filters, setFilters] = useState<UnitCodeFilters>({
    unitName: '',
    code: '',
    email: '',
    mobile: '',
    grade: '',
    term: '',
    usageDateStart: '',
    usageDateEnd: '',
    creationDateStart: '',
    creationDateEnd: '',
    codeType: '',
    userName: '',
    teacherId: ''
  });
  
  const [appliedFilters, setAppliedFilters] = useState<UnitCodeFilters>({
    unitName: '',
    code: '',
    email: '',
    mobile: '',
    grade: '',
    term: '',
    usageDateStart: '',
    usageDateEnd: '',
    creationDateStart: '',
    creationDateEnd: '',
    codeType: '',
    userName: '',
    teacherId: ''
  });
  
  // Sorting
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: null,
    direction: 'asc'
  });
  
  // Modals
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showGeneralCodeModal, setShowGeneralCodeModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Form data
  const [generateForm, setGenerateForm] = useState<GenerateCodeFormData>({
    unitId: '',
    count: '10'
  });
  
  const [generalCodeForm, setGeneralCodeForm] = useState<GenerateGeneralCodeFormData>({
    unitId: '',
    code: ''
  });
  
  // Course and units data
  const [courses, setCourses] = useState<CourseNew[]>([]);
  const [units, setUnits] = useState<CourseTreeUnit[]>([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState<string>('');
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [selectedTerm, setSelectedTerm] = useState<string>('');
  const [selectedCourse, setSelectedCourse] = useState<string>('');
  
  // Delete state
  const [codeToDelete, setCodeToDelete] = useState<GetUnitCodeDto | null>(null);

  // State for grades, teachers, and subjects
  const [grades, setGrades] = useState<Grade[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loadingGrades, setLoadingGrades] = useState(false);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  const [loadingSubjects, setLoadingSubjects] = useState(false);

  // Fetch grades
  const fetchGrades = async () => {
    setLoadingGrades(true);
    try {
      const response = await authFetch(apiUrl('Grade'), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setGrades(data.data);
      }
    } catch (error) {
      console.error('Error fetching grades:', error);
      setGrades([]);
    } finally {
      setLoadingGrades(false);
    }
  };

  // Fetch teachers
  const fetchTeachers = async () => {
    setLoadingTeachers(true);
    try {
      const response = await authFetch(apiUrl('Teacher'), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setTeachers(data.data);
      }
    } catch (error) {
      console.error('Error fetching teachers:', error);
      setTeachers([]);
    } finally {
      setLoadingTeachers(false);
    }
  };

  // Fetch subjects for a teacher
  const fetchSubjects = async (teacherId: string) => {
    if (!teacherId) return;
    
    setLoadingSubjects(true);
    try {
      const response = await authFetch(apiUrl(`Teacher/${teacherId}/subjects`), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSubjects(data.data);
      }
    } catch (error) {
      console.error('Error fetching subjects:', error);
      setSubjects([]);
    } finally {
      setLoadingSubjects(false);
    }
  };

  // Event handlers for new cascading structure
  const handleTeacherChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedTeacher(e.target.value);
    setSelectedSubject('');
    setSelectedTerm('');
    setSelectedCourse('');
    setGenerateForm(prev => ({ ...prev, unitId: '' }));
    setGeneralCodeForm(prev => ({ ...prev, unitId: '' }));
    setSubjects([]);
    setCourses([]);
    setUnits([]);
    if (e.target.value) {
      fetchSubjects(e.target.value);
    }
  };

  const handleSubjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSubject(e.target.value);
    setSelectedTerm('');
    setSelectedCourse('');
    setGenerateForm(prev => ({ ...prev, unitId: '' }));
    setGeneralCodeForm(prev => ({ ...prev, unitId: '' }));
    setCourses([]);
    setUnits([]);
  };

  const handleTermChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedTerm(e.target.value);
    setSelectedCourse('');
    setGenerateForm(prev => ({ ...prev, unitId: '' }));
    setGeneralCodeForm(prev => ({ ...prev, unitId: '' }));
    setUnits([]);
    if (selectedTeacher && selectedSubject && e.target.value) {
      fetchCourses(selectedTeacher, selectedSubject, e.target.value);
    }
  };

  const handleCourseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCourse(e.target.value);
    setGenerateForm(prev => ({ ...prev, unitId: '' }));
    setGeneralCodeForm(prev => ({ ...prev, unitId: '' }));
    if (e.target.value) {
      fetchUnits(e.target.value);
    }
  };

  const handleUnitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setGenerateForm(prev => ({ ...prev, unitId: e.target.value }));
  };

  const handleGeneralUnitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setGeneralCodeForm(prev => ({ ...prev, unitId: e.target.value }));
  };
  
  // Auto-hide success messages
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Fetch grades and teachers on component mount
  useEffect(() => {
    fetchGrades();
    fetchTeachers();
  }, []);
  
  // Fetch courses for modals using new API structure
  const fetchCourses = async (teacherId: string, subjectId: string, term: string) => {
    if (!teacherId || !subjectId || !term) return;
    
    setLoadingCourses(true);
    try {
      const response = await authFetch(
        apiUrl(`Course/all?Term=${term}&SubjectId=${subjectId}&TeacherId=${teacherId}`),
        {
          headers: {
            'Authorization': `Bearer ${authService.getToken()}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCourses(data.data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      setCourses([]);
    } finally {
      setLoadingCourses(false);
    }
  };

  // Fetch units for selected course
  const fetchUnits = async (courseId: string) => {
    if (!courseId) return;
    
    setLoadingUnits(true);
    try {
      const response = await courseTreeApi.getCourseTree(courseId);
      
      if (response.success) {
        setUnits(response.data.units || []);
      }
    } catch (error) {
      console.error('Error fetching units:', error);
      setUnits([]);
    } finally {
      setLoadingUnits(false);
    }
  };
  
  const fetchCodes = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Build the URL with query parameters based on the active tab
      let url = `UnitCode/GetAllCodes?pageNumber=${currentPage}&pageSize=${pageSize}`;
      
      // Set codeType based on active tab
      if (activeTab === 'codes') {
        url += '&codeType=0'; // Regular unit codes
      } else if (activeTab === 'general-codes') {
        url += '&codeType=1'; // General unit codes
      }
      
      // Add teacher filtering for Teacher role
      const teacherId = isTeacher() ? getTeacherId() : null;
      if (teacherId) {
        url += `&teacherId=${teacherId}`;
      }
      
      // Add filter parameters if they exist
      if (appliedFilters.unitName) url += `&unitName=${encodeURIComponent(appliedFilters.unitName)}`;
      if (appliedFilters.code) url += `&code=${encodeURIComponent(appliedFilters.code)}`;
      if (appliedFilters.email) url += `&studentEmail=${encodeURIComponent(appliedFilters.email)}`;
      if (appliedFilters.mobile) url += `&studentPhone=${encodeURIComponent(appliedFilters.mobile)}`;
      if (appliedFilters.grade) url += `&grade=${encodeURIComponent(appliedFilters.grade)}`;
      if (appliedFilters.term) url += `&courseTerm=${encodeURIComponent(appliedFilters.term)}`;
      if (appliedFilters.teacherId) url += `&teacherId=${encodeURIComponent(appliedFilters.teacherId)}`;
      
      // For the codes tab, we can filter by isBurned status
      if (activeTab === 'codes' && appliedFilters.codeType) {
        // Use the codeType filter value as isBurned status for regular codes
        if (appliedFilters.codeType === 'used') {
          url += '&isBurned=true';
        } else if (appliedFilters.codeType === 'unused') {
          url += '&isBurned=false';
        }
      }
      
      const response = await authFetch(apiUrl(url), {
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Map API response to our interface using the new response structure
          const formattedCodes = data.data.data.map((code: any) => ({
            id: code.id,
            code: code.code,
            createdAt: code.createdAt,
            usageDate: code.usageDate,
            unitId: code.unitId,
            unitName: code.unitName,
            grade: code.grade,
            courseName: code.courseName,
            studentId: code.studentId,
            studentName: code.studentName,
            studentEmail: code.studentEmail,
            studentPhone: code.studentPhone,
            courseTerm: code.courseTerm,
            isBurned: code.isBurned
          }));
          
          setCodes(formattedCodes);
          setTotalCount(data.data.totalCount || 0);
          setTotalPages(data.data.totalPages || 1);
        } else {
          setError(data.message || 'Failed to fetch codes');
        }
      } else {
        throw new Error('Failed to fetch codes');
      }
    } catch (err) {
      setError('Failed to load codes. Please try again.');
      console.error('Error fetching codes:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, appliedFilters, currentPage, getTeacherId, isTeacher, pageSize]);
  
  const fetchStudents = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Build the URL with query parameters
      let url = `UnitCode/GetAllStudentEnterCode?pageNumber=${currentPage}&pageSize=${pageSize}`;
      
      // Add filter parameters if they exist
      if (appliedFilters.unitName) url += `&unitName=${encodeURIComponent(appliedFilters.unitName)}`;
      if (appliedFilters.code) url += `&code=${encodeURIComponent(appliedFilters.code)}`;
      if (appliedFilters.userName) url += `&userName=${encodeURIComponent(appliedFilters.userName)}`;
      if (appliedFilters.email) url += `&email=${encodeURIComponent(appliedFilters.email)}`;
      if (appliedFilters.mobile) url += `&phoneNumber=${encodeURIComponent(appliedFilters.mobile)}`;
      if (appliedFilters.grade) url += `&academicYear=${encodeURIComponent(appliedFilters.grade)}`;
      if (appliedFilters.term) url += `&term=${encodeURIComponent(appliedFilters.term)}`;
      if (appliedFilters.usageDateStart) url += `&usageDateStart=${encodeURIComponent(appliedFilters.usageDateStart)}`;
      if (appliedFilters.usageDateEnd) url += `&usageDateEnd=${encodeURIComponent(appliedFilters.usageDateEnd)}`;
      if (appliedFilters.creationDateStart) url += `&creationDateStart=${encodeURIComponent(appliedFilters.creationDateStart)}`;
      if (appliedFilters.creationDateEnd) url += `&creationDateEnd=${encodeURIComponent(appliedFilters.creationDateEnd)}`;
      
      const response = await authFetch(apiUrl(url), {
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Map API response to our interface using the new response structure
          const formattedStudents = data.data.students.map((student: any) => ({
            studentId: student.studentId,
            userName: student.userName,
            email: student.email,
            phoneNumber: student.phoneNumber,
            academicYear: student.academicYear,
            codeId: student.codeId,
            code: student.code,
            usageDate: student.usageDate,
            creationDate: student.creationDate,
            unitName: student.unitName,
            term: student.term
          }));
          
          setStudents(formattedStudents);
          setTotalCount(data.data.totalCount || 0);
          setTotalPages(data.data.totalPages || 1);
        } else {
          setError(data.message || 'Failed to fetch students');
        }
      } else {
        throw new Error('Failed to fetch students');
      }
    } catch (err) {
      setError('Failed to load students. Please try again.');
      console.error('Error fetching students:', err);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, currentPage, pageSize]);

  const fetchGeneralCodes = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Build the URL with query parameters
      let url = `UnitCode/GetAllGeneralCodes?pageNumber=${currentPage}&pageSize=${pageSize}`;
      
      // Add filter parameters if they exist
      if (appliedFilters.unitName) url += `&unitName=${encodeURIComponent(appliedFilters.unitName)}`;
      if (appliedFilters.code) url += `&code=${encodeURIComponent(appliedFilters.code)}`;
      if (appliedFilters.grade) url += `&grade=${encodeURIComponent(appliedFilters.grade)}`;
      if (appliedFilters.term) url += `&courseTerm=${encodeURIComponent(appliedFilters.term)}`;
      
      const response = await authFetch(apiUrl(url), {
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Map API response to our interface using the new response structure
          const formattedGeneralCodes = data.data.generalCodes.map((code: any) => ({
            id: code.id,
            code: code.code,
            createdAt: code.createdAt,
            usageDate: code.usageDate,
            unitId: code.unitId,
            unitName: code.unitName,
            grade: code.grade,
            courseName: code.courseName,
            studentId: code.studentId,
            studentName: code.studentName,
            studentEmail: code.studentEmail,
            studentPhone: code.studentPhone,
            courseTerm: code.courseTerm,
            isBurned: code.isBurned
          }));
          
          setGeneralCodes(formattedGeneralCodes);
          setTotalCount(data.data.totalCount || 0);
          setTotalPages(data.data.totalPages || 1);
        } else {
          setError(data.message || 'Failed to fetch general codes');
        }
      } else {
        throw new Error('Failed to fetch general codes');
      }
    } catch (err) {
      setError('Failed to load general codes. Please try again.');
      console.error('Error fetching general codes:', err);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, currentPage, pageSize]);

  useEffect(() => {
    if (activeTab === 'codes') {
      fetchCodes();
    } else if (activeTab === 'students') {
      fetchStudents();
    } else if (activeTab === 'general-codes') {
      fetchGeneralCodes();
    }
  }, [activeTab, fetchCodes, fetchGeneralCodes, fetchStudents]);
  
  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSearch = () => {
    setAppliedFilters(filters);
    setCurrentPage(1);
  };
  
  // Helper to reset filters and pagination
  const resetFiltersAndPagination = () => {
    const resetFilters: UnitCodeFilters = {
      unitName: '',
      code: '',
      email: '',
      mobile: '',
      grade: '',
      term: '',
      usageDateStart: '',
      usageDateEnd: '',
      creationDateStart: '',
      creationDateEnd: '',
      codeType: '',
      userName: '',
      teacherId: ''
    };
    setFilters(resetFilters);
    setAppliedFilters(resetFilters);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    resetFiltersAndPagination();
  };
  
  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: key as SortableFields, direction });
  };
  
  const downloadAsCSV = (codes: any[], unitName: string) => {
    // Create CSV content with proper encoding for Arabic text
    const headers = ['Code', 'Unit Name', 'Grade', 'Term', 'Creation Date'];
    const rows = codes.map(code => [
      code.code || code.Code,
      `"${unitName.replace(/"/g, '""')}"`, // Properly escape quotes and wrap in quotes for Arabic text
      code.grade || code.Grade || '',
      code.courseTerm || code.Term || '',
      new Date().toLocaleDateString('ar-EG')
    ]);
    
    // Add BOM for proper UTF-8 encoding
    const BOM = '\uFEFF';
    const csvContent = BOM + [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Create blob with UTF-8 encoding
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    const filename = `${unitName}-codes-${new Date().toISOString().split('T')[0]}.csv`;
    
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleGenerateCodes = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    try {
      const response = await unitCodesApi.generateCodeUnit(
        parseInt(generateForm.unitId),
        parseInt(generateForm.count)
      );
      
      if (response.success) {
        // Get the selected unit name for the filename
        const selectedUnit = units.find(unit => unit.id.toString() === generateForm.unitId);
        const unitName = selectedUnit ? selectedUnit.unitName : 'unit-codes';
        
        // Download the generated codes as CSV if data is available
        if (response.data && Array.isArray(response.data)) {
          downloadAsCSV(response.data, unitName);
          setSuccessMessage(`تم إنشاء ${generateForm.count} كود وتحميلها بنجاح!`);
        } else {
          setSuccessMessage(`تم إنشاء ${generateForm.count} كود بنجاح!`);
        }
        
        setShowGenerateModal(false);
        setGenerateForm({ unitId: '', count: '10' });
        fetchCodes();
      } else {
        setError(response.message || 'Failed to generate codes');
      }
    } catch (err) {
      setError('Failed to generate codes. Please try again.');
      console.error('Error generating codes:', err);
    }
  };
  
  const handleGenerateGeneralCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    try {
      const response = await unitCodesApi.generateGeneralCode(
        parseInt(generalCodeForm.unitId),
        generalCodeForm.code
      );
      
      if (response.success) {
        // Get the selected unit name for the filename
        const selectedUnit = units.find(unit => unit.id.toString() === generalCodeForm.unitId);
        const unitName = selectedUnit ? selectedUnit.unitName : 'general-code';
        
        // Download the generated general code as CSV if data is available
        if (response.data && (Array.isArray(response.data) || response.data.code)) {
          const codeData = Array.isArray(response.data) ? response.data : [response.data];
          downloadAsCSV(codeData, `${unitName}-general`);
          setSuccessMessage('تم إنشاء الكود العام وتحميله بنجاح!');
        } else {
          setSuccessMessage('تم إنشاء الكود العام بنجاح!');
        }
        
        setShowGeneralCodeModal(false);
        setGeneralCodeForm({ unitId: '', code: '' });
        fetchCodes();
      } else {
        setError(response.message || 'Failed to generate general code');
      }
    } catch (err) {
      setError('Failed to generate general code. Please try again.');
      console.error('Error generating general code:', err);
    }
  };
  
  const handleDeleteCode = async () => {
    if (!codeToDelete) return;
    
    try {
      const response = await unitCodesApi.deleteCode(codeToDelete.id);
      
      if (response.success) {
        setSuccessMessage('تم حذف الكود بنجاح!');
        setShowDeleteConfirm(false);
        setCodeToDelete(null);
        fetchCodes();
      } else {
        setError(response.message || 'Failed to delete code');
      }
    } catch (err) {
      setError('Failed to delete code. Please try again.');
      console.error('Error deleting code:', err);
    }
  };
  
  const openDeleteModal = (code: GetUnitCodeDto) => {
    setCodeToDelete(code);
    setShowDeleteConfirm(true);
  };
  
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleOpenGenerateModal = () => {
    setShowGenerateModal(true);
    // Reset form states
    const teacherId = isTeacher() ? getTeacherId() : '';
    setSelectedTeacher(teacherId || '');
    setSelectedSubject('');
    setSelectedTerm('');
    setSelectedCourse('');
    setGenerateForm({ unitId: '', count: '10' });
    setSubjects([]);
    setCourses([]);
    setUnits([]);
    
    // Auto-fetch subjects if user is a teacher
    if (isTeacher() && getTeacherId()) {
      fetchSubjects(getTeacherId()!);
    }
  };

  const handleOpenGeneralCodeModal = () => {
    setShowGeneralCodeModal(true);
    // Reset form states
    const teacherId = isTeacher() ? getTeacherId() : '';
    setSelectedTeacher(teacherId || '');
    setSelectedSubject('');
    setSelectedTerm('');
    setSelectedCourse('');
    setGeneralCodeForm({ unitId: '', code: '' });
    setSubjects([]);
    setCourses([]);
    setUnits([]);
    
    // Auto-fetch subjects if user is a teacher
    if (isTeacher() && getTeacherId()) {
      fetchSubjects(getTeacherId()!);
    }
  };

  const handleCloseGenerateModal = () => {
    setShowGenerateModal(false);
    // Reset form states
    setSelectedTeacher('');
    setSelectedSubject('');
    setSelectedTerm('');
    setSelectedCourse('');
    setGenerateForm({ unitId: '', count: '10' });
    setSubjects([]);
    setCourses([]);
    setUnits([]);
  };

  const handleCloseGeneralCodeModal = () => {
    setShowGeneralCodeModal(false);
    // Reset form states
    setSelectedTeacher('');
    setSelectedSubject('');
    setSelectedTerm('');
    setSelectedCourse('');
    setGeneralCodeForm({ unitId: '', code: '' });
    setSubjects([]);
    setCourses([]);
    setUnits([]);
  };
  
  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('ar-EG');
  };
  
  // Sort data
  const getSortedData = () => {
    if (activeTab === 'students') {
      let sortedStudents = [...students];
      if (sortConfig.key) {
        sortedStudents.sort((a, b) => {
          const aValue = String((a as any)[sortConfig.key!] || '');
          const bValue = String((b as any)[sortConfig.key!] || '');
          
          return sortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        });
      }
      return sortedStudents;
    } else if (activeTab === 'codes') {
      let sortedCodes = [...codes];
      if (sortConfig.key) {
        sortedCodes.sort((a, b) => {
          const aValue = String((a as any)[sortConfig.key!] || '');
          const bValue = String((b as any)[sortConfig.key!] || '');
          
          return sortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        });
      }
      return sortedCodes;
    } else {
      let sortedGeneralCodes = [...generalCodes];
      if (sortConfig.key) {
        sortedGeneralCodes.sort((a, b) => {
          const aValue = String((a as any)[sortConfig.key!] || '');
          const bValue = String((b as any)[sortConfig.key!] || '');
          
          return sortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        });
      }
      return sortedGeneralCodes;
    }
  };
  
  const sortedData = getSortedData();
  
  const handleGeneralCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGeneralCodeForm(prev => ({ ...prev, code: e.target.value }));
  };
  
  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">أكواد المحاضرات</h1>
              <p className="mt-1 text-sm text-gray-600">إدارة أكواد المحاضرات</p>
            </div>
            <div className="flex space-x-2 space-x-reverse">
              <button
                onClick={handleOpenGenerateModal}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                إنشاء أكواد
              </button>
              <button
                onClick={handleOpenGeneralCodeModal}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                إنشاء كود عام
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Alert */}
        {error && (
          <div className="mb-6 rounded-lg bg-red-50 p-4">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
              <div className="mr-3">
                <h3 className="text-sm font-medium text-red-800">خطأ</h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Success Alert */}
        {successMessage && (
          <div className="mb-6 rounded-lg bg-green-50 p-4">
            <div className="flex">
              <CheckCircleIcon className="h-5 w-5 text-green-400" />
              <div className="mr-3">
                <p className="text-sm font-medium text-green-800">{successMessage}</p>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-reverse space-x-8" aria-label="Tabs">
            <button
              onClick={() => {
                resetFiltersAndPagination();
                setActiveTab('codes');
              }}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'codes'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              أكواد المحاضرات
            </button>
            <button
              onClick={() => {
                resetFiltersAndPagination();
                setActiveTab('students');
              }}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'students'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              الطلاب المشتركون
            </button>
            <button
              onClick={() => {
                resetFiltersAndPagination();
                setActiveTab('general-codes');
              }}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'general-codes'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              الأكواد العامة
            </button>
          </nav>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">فلترة النتائج</h3>
          </div>
          <div className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  اسم المحاضرة
                </label>
                <input
                  type="text"
                  name="unitName"
                  value={filters.unitName}
                  onChange={handleFilterChange}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="البحث المحاضرة..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  الكود
                </label>
                <input
                  type="text"
                  name="code"
                  value={filters.code}
                  onChange={handleFilterChange}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="البحث بالكود..."
                />
              </div>
              
              {activeTab === 'codes' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    حالة الاستخدام
                  </label>
                  <select
                    name="codeType"
                    value={filters.codeType}
                    onChange={handleFilterChange}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  >
                    <option value="">جميع الحالات</option>
                    <option value="used">مستخدم</option>
                    <option value="unused">غير مستخدم</option>
                  </select>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  الصف الدراسي
                </label>
                <select
                  name="grade"
                  value={filters.grade}
                  onChange={handleFilterChange}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  disabled={loadingGrades}
                >
                  <option value="">
                    {loadingGrades ? 'جاري تحميل الصفوف...' : 'جميع الصفوف'}
                  </option>
                  {grades.map((grade) => (
                    <option key={grade.id} value={grade.id}>
                      {grade.name}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Teacher Filter - Only for Moderators */}
              {isModerator() && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">المدرس</label>
                  <select
                    name="teacherId"
                    value={filters.teacherId}
                    onChange={handleFilterChange}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    disabled={loadingTeachers}
                  >
                    <option value="">
                      {loadingTeachers ? 'جاري تحميل المدرسين...' : 'كل المدرسين'}
                    </option>
                    {teachers.map((teacher) => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.fullName}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Teacher Info Display - Only for Teachers */}
              {isTeacher() && user && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">المدرس</label>
                  <div className="w-full px-3 py-2 border rounded-lg bg-gray-50 text-gray-700">
                    {user.name}
                  </div>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  الفصل الدراسي
                </label>
                <select
                  name="term"
                  value={filters.term}
                  onChange={handleFilterChange}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                >
                  <option value="">جميع الفصول</option>
                  {TERM_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              
              {activeTab === 'students' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      اسم المستخدم
                    </label>
                    <input
                      type="text"
                      name="userName"
                      value={filters.userName || ''}
                      onChange={handleFilterChange}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      placeholder="البحث باسم المستخدم..."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      البريد الإلكتروني
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={filters.email}
                      onChange={handleFilterChange}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      placeholder="البحث بالبريد..."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      رقم الهاتف
                    </label>
                    <input
                      type="text"
                      name="mobile"
                      value={filters.mobile}
                      onChange={handleFilterChange}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      placeholder="البحث برقم الهاتف..."
                    />
                  </div>
                </>
              )}
            </div>
            
            <div className="mt-4 flex space-x-2 space-x-reverse">
              <button
                onClick={handleSearch}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                بحث
              </button>
              <button
                onClick={handleResetFilters}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                إعادة تعيين
              </button>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <span className="text-lg font-medium text-gray-900">
                {activeTab === 'codes' ? 'أكواد المحاضرات' : activeTab === 'students' ? 'الطلاب المشتركون' : 'الأكواد العامة'}
              </span>
              <span className="text-sm text-gray-500">
                المجموع: {totalCount}
              </span>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {activeTab === 'codes' || activeTab === 'general-codes' ? (
                    <>
                      <th 
                        scope="col" 
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('code')}
                      >
                        الكود
                        {sortConfig.key === 'code' && (
                          <span className="mr-1">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th 
                        scope="col" 
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('unitName')}
                      >
                        اسم المحاضرة
                        {sortConfig.key === 'unitName' && (
                          <span className="mr-1">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th 
                        scope="col" 
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('courseName')}
                      >
                        اسم الباقة
                        {sortConfig.key === 'courseName' && (
                          <span className="mr-1">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th 
                        scope="col" 
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('grade')}
                      >
                        الصف
                        {sortConfig.key === 'grade' && (
                          <span className="mr-1">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th 
                        scope="col" 
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('courseTerm')}
                      >
                        الفصل
                        {sortConfig.key === 'courseTerm' && (
                          <span className="mr-1">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th 
                        scope="col" 
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('studentName')}
                      >
                        اسم الطالب
                        {sortConfig.key === 'studentName' && (
                          <span className="mr-1">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th 
                        scope="col" 
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('createdAt')}
                      >
                        تاريخ الإنشاء
                        {sortConfig.key === 'createdAt' && (
                          <span className="mr-1">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th 
                        scope="col" 
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('usageDate')}
                      >
                        تاريخ الاستخدام
                        {sortConfig.key === 'usageDate' && (
                          <span className="mr-1">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        الإجراءات
                      </th>
                    </>
                  ) : (
                    <>
                      <th 
                        scope="col" 
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('userName')}
                      >
                        اسم المستخدم
                        {sortConfig.key === 'userName' && (
                          <span className="mr-1">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th 
                        scope="col" 
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('email')}
                      >
                        البريد الإلكتروني
                        {sortConfig.key === 'email' && (
                          <span className="mr-1">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th 
                        scope="col" 
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('phoneNumber')}
                      >
                        رقم الهاتف
                        {sortConfig.key === 'phoneNumber' && (
                          <span className="mr-1">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th 
                        scope="col" 
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('code')}
                      >
                        الكود
                        {sortConfig.key === 'code' && (
                          <span className="mr-1">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th 
                        scope="col" 
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('unitName')}
                      >
                        اسم المحاضرة
                        {sortConfig.key === 'unitName' && (
                          <span className="mr-1">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th 
                        scope="col" 
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('usageDate')}
                      >
                        تاريخ الاستخدام
                        {sortConfig.key === 'usageDate' && (
                          <span className="mr-1">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={activeTab === 'codes' || activeTab === 'general-codes' ? 9 : 6} className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                      </div>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={activeTab === 'codes' || activeTab === 'general-codes' ? 9 : 6} className="px-6 py-4 text-center text-red-500">
                      {error}
                    </td>
                  </tr>
                ) : sortedData.length === 0 ? (
                  <tr>
                    <td colSpan={activeTab === 'codes' || activeTab === 'general-codes' ? 9 : 6} className="px-6 py-4 text-center text-gray-500">
                      {activeTab === 'codes' ? 'لا توجد أكواد' : activeTab === 'students' ? 'لا توجد بيانات طلاب' : 'لا توجد أكواد عامة'}
                    </td>
                  </tr>
                ) : (
                  activeTab === 'codes' || activeTab === 'general-codes' ? (
                    (sortedData as GetUnitCodeDto[]).map((code) => (
                      <tr key={code.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {code.code}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {code.unitName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {code.courseName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {code.grade || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {code.courseTerm}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {code.studentName || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDateTime(code.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {code.isBurned ? (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                              {code.usageDate ? formatDateTime(code.usageDate) : 'مستخدم'}
                            </span>
                          ) : (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                              غير مستخدم
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => openDeleteModal(code)}
                            className="text-red-600 hover:text-red-900 p-1"
                            title="حذف"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    (sortedData as StudentWithUnitCodesDto[]).map((student) => (
                      <tr key={student.codeId} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {student.userName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {student.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {student.phoneNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {student.code}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {student.unitName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {student.usageDate ? formatDateTime(student.usageDate) : '-'}
                        </td>
                      </tr>
                    ))
                  )
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  السابق
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  التالي
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    عرض <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> إلى{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * pageSize, totalCount)}
                    </span>{' '}
                    من <span className="font-medium">{totalCount}</span> نتائج
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      السابق
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === currentPage
                            ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      التالي
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Generate Codes Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseGenerateModal}></div>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-right overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleGenerateCodes}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                        إنشاء أكواد وحدة
                      </h3>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            المعلم *
                          </label>
                          <select
                            value={selectedTeacher}
                            onChange={handleTeacherChange}
                            required
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            disabled={loadingTeachers || isTeacher()}
                          >
                            <option value="">
                              {loadingTeachers ? 'جاري تحميل المعلمين...' : 'اختر المعلم'}
                            </option>
                            {teachers.map(teacher => (
                              <option key={teacher.id} value={teacher.id}>
                                {teacher.fullName}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            المادة *
                          </label>
                          <select
                            value={selectedSubject}
                            onChange={handleSubjectChange}
                            required
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            disabled={!selectedTeacher || loadingSubjects}
                          >
                            <option value="">
                              {loadingSubjects ? 'جاري تحميل المواد...' : 'اختر المادة'}
                            </option>
                            {subjects.map(subject => (
                              <option key={subject.id} value={subject.id}>
                                {subject.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            الفصل الدراسي *
                          </label>
                          <select
                            value={selectedTerm}
                            onChange={handleTermChange}
                            required
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            disabled={!selectedSubject}
                          >
                            <option value="">اختر الفصل الدراسي</option>
                            <option value="First">الفصل الأول</option>
                            <option value="Second">الفصل الثاني</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            الدورة *
                          </label>
                          <select
                            value={selectedCourse}
                            onChange={handleCourseChange}
                            required
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            disabled={!selectedTeacher || !selectedSubject || !selectedTerm || loadingCourses}
                          >
                            <option value="">
                              {loadingCourses ? 'جاري تحميل الباقات...' : 'اختر الدورة'}
                            </option>
                            {courses.map(course => (
                              <option key={course.id} value={course.id}>
                                {course.courseName}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                          المحاضرة *
                          </label>
                          <select
                            value={generateForm.unitId}
                            onChange={handleUnitChange}
                            required
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            disabled={!selectedCourse || loadingUnits}
                          >
                            <option value="">
                              {loadingUnits ? 'جاري تحميل المحاضرات...' : 'اختر المحاضرة'}
                            </option>
                            {units.map(unit => (
                              <option key={unit.id} value={unit.id}>
                                {unit.unitName}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            عدد الأكواد *
                          </label>
                          <input
                            type="number"
                            value={generateForm.count}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGenerateForm(prev => ({ ...prev, count: e.target.value }))}
                            required
                            min="1"
                            max="100"
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            placeholder="10"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    إنشاء الأكواد
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseGenerateModal}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    إلغاء
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Generate General Code Modal */}
      {showGeneralCodeModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseGeneralCodeModal}></div>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-right overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleGenerateGeneralCode}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                        إنشاء كود عام
                      </h3>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            المعلم *
                          </label>
                          <select
                            value={selectedTeacher}
                            onChange={handleTeacherChange}
                            required
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            disabled={loadingTeachers || isTeacher()}
                          >
                            <option value="">
                              {loadingTeachers ? 'جاري تحميل المعلمين...' : 'اختر المعلم'}
                            </option>
                            {teachers.map(teacher => (
                              <option key={teacher.id} value={teacher.id}>
                                {teacher.fullName}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            المادة *
                          </label>
                          <select
                            value={selectedSubject}
                            onChange={handleSubjectChange}
                            required
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            disabled={!selectedTeacher || loadingSubjects}
                          >
                            <option value="">
                              {loadingSubjects ? 'جاري تحميل المواد...' : 'اختر المادة'}
                            </option>
                            {subjects.map(subject => (
                              <option key={subject.id} value={subject.id}>
                                {subject.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            الفصل الدراسي *
                          </label>
                          <select
                            value={selectedTerm}
                            onChange={handleTermChange}
                            required
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            disabled={!selectedSubject}
                          >
                            <option value="">اختر الفصل الدراسي</option>
                            <option value="First">الفصل الأول</option>
                            <option value="Second">الفصل الثاني</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            الدورة *
                          </label>
                          <select
                            value={selectedCourse}
                            onChange={handleCourseChange}
                            required
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            disabled={!selectedTeacher || !selectedSubject || !selectedTerm || loadingCourses}
                          >
                            <option value="">
                              {loadingCourses ? 'جاري تحميل الباقات...' : 'اختر الدورة'}
                            </option>
                            {courses.map(course => (
                              <option key={course.id} value={course.id}>
                                {course.courseName}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                          المحاضرة *
                          </label>
                          <select
                            value={generalCodeForm.unitId}
                            onChange={handleGeneralUnitChange}
                            required
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            disabled={!selectedCourse || loadingUnits}
                          >
                            <option value="">
                              {loadingUnits ? 'جاري تحميل المحاضرات...' : 'اختر المحاضرة'}
                            </option>
                            {units.map(unit => (
                              <option key={unit.id} value={unit.id}>
                                {unit.unitName}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            الكود *
                          </label>
                          <input
                            type="text"
                            value={generalCodeForm.code}
                            onChange={handleGeneralCodeChange}
                            required
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            placeholder="أدخل الكود العام"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    إنشاء الكود
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseGeneralCodeModal}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    إلغاء
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && codeToDelete && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowDeleteConfirm(false)}></div>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-right overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:mr-4 sm:text-right">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      حذف الكود
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        هل أنت متأكد من حذف الكود "{codeToDelete.code}"؟ لا يمكن التراجع عن هذا الإجراء.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleDeleteCode}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  حذف
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setCodeToDelete(null);
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnitCodes; 